import type { PipelineOperatorAnnotated } from './report-pipelines.ts';
import type { ColumnID, TableID } from './report-pipelines.ts';
import { emissionsStandardPollutants } from '../emissions/emissions-standard-pollutants.ts';

export interface BuiltinReport {
  id: string;
  name: string;
  description: string;
  pipelineOperators: PipelineOperatorAnnotated[];
}

const orderColumnsByStandardChemicals: PipelineOperatorAnnotated = {
  notes: 'Put standard chemical columns first',
  config: {
    type: 'reorder',
    columnIds: ['Period', 'Month', ...emissionsStandardPollutants.map((p) => p.key as string)] as ColumnID[],
  },
};

export const builtinReports: BuiltinReport[] = [
  {
    id: '12-month-rolling',
    name: '12 Month Rolling Emissions',
    description: '12-month rolling sum of emissions.',
    pipelineOperators: [
      {
        notes: 'Start with emissions data',
        config: {
          type: 'from',
          tableID: 'Emissions Data' as TableID,
        },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to monthly',
        config: { type: 'compute', newColumnID: 'Month' as ColumnID, name: 'Month', formula: 'Period -> calmonth' },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Then group by Month and Chemical',
        config: {
          type: 'group',
          columnIds: ['Month', 'Chemical'] as ColumnID[],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'Tons_Sum' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      // TODO: 3. Emissions - We may want to create some sort of UI around this form of "window":
      {
        notes: 'Group by chemical for rolling window',
        config: {
          type: 'group',
          columnIds: ['Chemical'] as ColumnID[],
          pipelineOperators: [
            {
              notes: 'Sort by month ascending',
              config: {
                type: 'sort',
                columns: [
                  {
                    columnID: 'Month' as ColumnID,
                    direction: 'asc',
                  },
                ],
              },
            },
            {
              notes: '12-month rolling sum of Tons_Sum',
              config: {
                type: 'window',
                strict: false,
                preceding: 11,
                following: 0,
                columns: [
                  {
                    sourceColumnID: 'Tons_Sum' as ColumnID,
                    destinationColumnID: 'Tons_Rolling' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
            {
              notes: 'Pluck out Tons_Rolling',
              config: {
                type: 'select',
                columnIds: ['Month', 'Tons_Rolling'] as ColumnID[],
              },
            },
          ],
        },
      },
      {
        notes: 'Sort by Chemical and Month ascending',
        config: {
          type: 'sort',
          columns: [
            {
              columnID: 'Chemical' as ColumnID,
              direction: 'asc',
            },

            {
              columnID: 'Month' as ColumnID,
              direction: 'asc',
            },
          ],
        },
      },
      {
        notes: 'Pivot',
        config: {
          type: 'pivot',
          groupByColumnIds: ['Month'] as ColumnID[],
          headerColumnID: 'Chemical' as ColumnID,
          aggregatedColumnID: 'Tons_Rolling' as ColumnID,
          func: 'sum',
        },
      },
      orderColumnsByStandardChemicals,
    ],
  },
  {
    id: 'emissions-by-period',
    name: 'Emissions by Period',
    description: 'Actual emissions pivoted by period with chemicals as rows.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Pivot by period',
        config: {
          type: 'pivot',
          groupByColumnIds: ['Period' as ColumnID],
          headerColumnID: 'Chemical' as ColumnID,
          aggregatedColumnID: 'Tons' as ColumnID,
          func: 'sum',
        },
      },
      orderColumnsByStandardChemicals,
      {
        notes: 'Sort by period',
        config: { type: 'sort', columns: [{ columnID: 'Period' as ColumnID, direction: 'asc' }] },
      },
    ],
  },
  {
    id: 'hap-summary',
    name: 'HAP Emissions by Period',
    description: 'Total actual emissions for Hazardous Air Pollutants only.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Only include HAP emissions',
        config: { type: 'filter', formula: `HAP = true` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Group by chemical and sum results',
        config: {
          type: 'group',
          columnIds: ['Chemical', 'Period'] as ColumnID[],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'TotalTons' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      {
        notes: 'Pivot by period',
        config: {
          type: 'pivot',
          groupByColumnIds: ['Period' as ColumnID],
          headerColumnID: 'Chemical' as ColumnID,
          aggregatedColumnID: 'TotalTons' as ColumnID,
          func: 'sum',
        },
      },
      orderColumnsByStandardChemicals,
      {
        notes: 'Sort by period',
        config: { type: 'sort', columns: [{ columnID: 'Period' as ColumnID, direction: 'asc' }] },
      },
    ],
  },
  {
    id: 'emissions-by-release-profile',
    name: 'Emissions by Release Profile',
    description: 'Total actual emissions grouped by release profile.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Group by release profile and sum results',
        config: {
          type: 'group',
          columnIds: ['ReleaseProfileName' as ColumnID, 'Chemical' as ColumnID],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'TotalTons' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      {
        notes: 'Sort by release profile then chemical',
        config: {
          type: 'sort',
          columns: [
            { columnID: 'ReleaseProfileName' as ColumnID, direction: 'asc' },
            { columnID: 'TotalTons' as ColumnID, direction: 'desc' },
          ],
        },
      },
      {
        notes: 'Reorder columns',
        config: {
          type: 'reorder',
          columnIds: ['ReleaseProfileName', 'Chemical', 'TotalTons'] as ColumnID[],
        },
      },
    ],
  },
  {
    id: 'emissions-by-unit',
    name: 'Emissions by Unit',
    description: 'Total actual emissions grouped by unit.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Group by unit and sum results',
        config: {
          type: 'group',
          columnIds: ['Unit' as ColumnID, 'Chemical' as ColumnID],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'TotalTons' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      {
        notes: 'Sort by unit then chemical',
        config: {
          type: 'sort',
          columns: [
            { columnID: 'Unit' as ColumnID, direction: 'asc' },
            { columnID: 'TotalTons' as ColumnID, direction: 'desc' },
          ],
        },
      },
      {
        notes: 'Reorder columns',
        config: {
          type: 'reorder',
          columnIds: ['Unit', 'Chemical', 'TotalTons'] as ColumnID[],
        },
      },
    ],
  },
  {
    id: 'emissions-by-period-and-unit',
    name: 'Emissions by Period and Unit',
    description: 'Total actual emissions grouped by period and unit.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Group by period, unit, and chemical and sum results',
        config: {
          type: 'group',
          columnIds: ['Unit' as ColumnID, 'Period' as ColumnID, 'Chemical' as ColumnID],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'TotalTons' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      {
        notes: 'Sort by period, unit, and then chemical',
        config: {
          type: 'sort',
          columns: [
            { columnID: 'Period' as ColumnID, direction: 'asc' },
            { columnID: 'Unit' as ColumnID, direction: 'asc' },
            { columnID: 'TotalTons' as ColumnID, direction: 'desc' },
          ],
        },
      },
      {
        notes: 'Reorder columns',
        config: {
          type: 'reorder',
          columnIds: ['Period', 'Unit', 'Chemical', 'TotalTons'] as ColumnID[],
        },
      },
    ],
  },
  {
    id: 'hap-emissions-by-unit',
    name: 'HAP Emissions by Unit',
    description: 'Total actual HAP emissions grouped by unit.',
    pipelineOperators: [
      {
        notes: 'Load emissions data',
        config: { type: 'from', tableID: 'Emissions Data' as TableID },
      },
      {
        notes: 'Only include HAP chemicals',
        config: {
          type: 'filter',
          formula: `HAP = true`,
        },
      },
      {
        notes: 'Only include Mass results',
        config: {
          type: 'filter',
          formula: `ResultDimensions = 'Mass'`,
        },
      },
      {
        notes: 'Only include actual emissions',
        config: { type: 'filter', formula: `Kind = 'Actual'` },
      },
      {
        notes: 'Convert to tons',
        config: { type: 'compute', newColumnID: 'Tons' as ColumnID, name: 'Tons', formula: 'Result -> tons' },
      },
      {
        notes: 'Group by unit and sum results',
        config: {
          type: 'group',
          columnIds: ['Unit' as ColumnID, 'Chemical' as ColumnID],
          pipelineOperators: [
            {
              notes: 'Sum the tons',
              config: {
                type: 'aggregate',
                columns: [
                  {
                    sourceColumnID: 'Tons' as ColumnID,
                    destinationColumnID: 'TotalTons' as ColumnID,
                    func: 'sum',
                  },
                ],
              },
            },
          ],
        },
      },
      {
        notes: 'Sort by unit then chemical',
        config: {
          type: 'sort',
          columns: [
            { columnID: 'Unit' as ColumnID, direction: 'asc' },
            { columnID: 'TotalTons' as ColumnID, direction: 'desc' },
          ],
        },
      },
      {
        notes: 'Reorder columns',
        config: {
          type: 'reorder',
          columnIds: ['Unit', 'Chemical', 'TotalTons'] as ColumnID[],
        },
      },
    ],
  },
];
